import type { CalendarProps } from 'antd';
import cn from 'classnames';
import { BaseCalendar, getDatePickerDateType, getDateType } from '../base-picker';
import styles from './calendar.module.css';
import { useGetCellWithAccessibility, useGetWeekSelectionModeValues } from './hooks';
import type { CalendarDateType } from './types';

export type CalendarProperties = {
  hideModeSwitch?: boolean;
  onChange: (value: Date) => void;
  selectionMode?: SelectionMode;
  value: Date;
} & Omit<CalendarProps<CalendarDateType>, 'onChange' | 'value'>;

type SelectionMode = 'day' | 'month' | 'week';

const Calendar = ({ hideModeSwitch, onChange, selectionMode = 'day', value, ...props }: CalendarProperties) => {
  const getCellWithAccessibility = useGetCellWithAccessibility();
  const getWeekSelectionModeValues = useGetWeekSelectionModeValues();

  return (
    <BaseCalendar
      className={cn(styles.calendar, {
        [styles['hide-mode-switch'] ?? '']: hideModeSwitch,
        [styles['month-selection-mode'] ?? '']: selectionMode === 'month',
        [styles['week-selection-mode'] ?? '']: selectionMode === 'week',
      })}
      fullCellRender={(current, info) => {
        const originWithTabIndex = getCellWithAccessibility({
          cell: info.originNode,
          current,
          onChange,
        });

        if (selectionMode !== 'week' || info.type !== 'date') {
          return originWithTabIndex;
        }

        const { isInWeek } = getWeekSelectionModeValues({
          current,
          value,
        });

        return (
          <div
            className={cn(styles['week-cell'], {
              [styles['selected-week'] ?? '']: isInWeek,
            })}
          >
            {originWithTabIndex}
          </div>
        );
      }}
      fullscreen={false}
      {...props}
      onChange={(newDate) => {
        onChange(getDateType(newDate));
      }}
      value={getDatePickerDateType(value)}
    />
  );
};

export default Calendar;
